/**
 * Language and direction hook.
 *
 * The one place that changes language, so `<html lang>`, `<html dir>` and localStorage cannot
 * drift apart from what i18next thinks is active.
 */

import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  type Direction,
  type Language,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  applyDocumentLanguage,
  directionOf,
  isSupported,
} from "./config";

export interface UseLanguageResult {
  language: Language;
  direction: Direction;
  isRtl: boolean;
  languages: readonly Language[];
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
}

export function useLanguage(): UseLanguageResult {
  const { i18n } = useTranslation();
  const language: Language = isSupported(i18n.language) ? i18n.language : "en";
  const direction = directionOf(language);

  // Covers the case where the language changed from outside this hook — another tab, or
  // i18next itself — so the document attributes still follow.
  useEffect(() => {
    applyDocumentLanguage(language);
  }, [language]);

  const setLanguage = useCallback(
    (next: Language) => {
      if (next === language) return;
      void i18n.changeLanguage(next);
      applyDocumentLanguage(next);
      try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
      } catch {
        // Private browsing or a storage quota: the language still changes for this session,
        // which matters more than remembering it.
      }
    },
    [i18n, language],
  );

  const toggleLanguage = useCallback(() => {
    setLanguage(language === "ar" ? "en" : "ar");
  }, [language, setLanguage]);

  return useMemo(
    () => ({
      language,
      direction,
      isRtl: direction === "rtl",
      languages: SUPPORTED_LANGUAGES,
      setLanguage,
      toggleLanguage,
    }),
    [language, direction, setLanguage, toggleLanguage],
  );
}
